import React, { useEffect, useState } from 'react';
import { Routes, Route, Navigate, useNavigate, useLocation, useSearchParams } from 'react-router-dom';
import { onAuthStateChanged } from 'firebase/auth';
import { doc, getDoc } from 'firebase/firestore';
import { auth, db } from './firebase';
import { LandingPage } from './AuthPage';
import AuthPage from './AuthPage';
import ClientApp, { PublicProgramPage, PublicCatalogPage } from './ClientApp';
import AdminPanel from './AdminPanel';

function FullScreenSpinner() {
  return (
    <div style={{ minHeight: '100vh', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', background: '#F2F6F7', gap: 16 }}>
      <svg viewBox="0 0 44 44" width="48" height="48" fill="none">
        <circle cx="22" cy="22" r="20" stroke="#9FCAD2" strokeWidth="1.5"/>
        <circle cx="22" cy="11.5" r="2.5" fill="#9FCAD2"/>
        <path d="M19.5 16L12 9" stroke="#9FCAD2" strokeWidth="1.5" strokeLinecap="round"/>
        <path d="M24.5 16L32 9" stroke="#9FCAD2" strokeWidth="1.5" strokeLinecap="round"/>
        <line x1="22" y1="14" x2="22" y2="27" stroke="#9FCAD2" strokeWidth="1.5" strokeLinecap="round"/>
        <line x1="22" y1="27" x2="15" y2="35" stroke="#9FCAD2" strokeWidth="1.5" strokeLinecap="round"/>
        <line x1="22" y1="27" x2="29" y2="35" stroke="#9FCAD2" strokeWidth="1.5" strokeLinecap="round"/>
      </svg>
      <div style={{ fontFamily: 'Inter, sans-serif', fontSize: 18, color: '#1F4E5A', letterSpacing: '0.02em' }}>Beyond The Body</div>
      <div style={{ width: 32, height: 32, border: '3px solid #E3EEF0', borderTopColor: '#4F9CA8', borderRadius: '50%', animation: 'spin 0.8s linear infinite' }} />
      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
    </div>
  );
}

// ─── ROUTE GUARDS ──────────────────────────────────────────────────────────────

// The auth funnel. Once a user is signed in, bounce them to wherever they were
// headed (the `next` deep link) or the catalog — admins go to the panel.
function SigninRoute({ user, isAdmin }) {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const next = params.get('next');
  const mode = params.get('mode');
  if (user) return <Navigate to={isAdmin ? '/admin' : (next || '/programs')} replace />;
  return <AuthPage skipLanding initialScreen={mode === 'signup' ? 'signup' : 'login'} onBack={() => navigate('/')} />;
}

// Client area. Logged-out visitors are sent to sign in with a `next` deep link so
// a shared program URL survives the round-trip. Admins are sent to the panel
// unless they're explicitly previewing as a client.
function ClientRoute({ user, isAdmin, adminPreview, onExitPreview }) {
  const navigate = useNavigate();
  const location = useLocation();
  if (!user) {
    // The programs catalog is public — logged-out visitors get the browse-only
    // catalog (sign-in still required to enrol or play). Every other client path
    // keeps the sign-in wall, with a `next` deep link to return after login.
    if (location.pathname === '/programs') return <PublicCatalogPage />;
    return <Navigate to={`/signin?next=${encodeURIComponent(location.pathname)}`} replace />;
  }
  if (isAdmin && !adminPreview) return <Navigate to="/admin" replace />;
  return (
    <>
      <ClientApp user={user} adminPreview={adminPreview} onGoHome={() => navigate('/welcome')} />
      {adminPreview && (
        <div style={{ position: 'fixed', bottom: 0, left: 0, right: 0, zIndex: 9999, background: '#1F4E5A', color: '#F2F6F7', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 16, padding: '10px 16px', fontFamily: 'Inter, sans-serif', fontSize: 14, boxShadow: '0 -2px 12px rgba(0,0,0,0.18)' }}>
          <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="#9FCAD2" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M1 12s4-7 11-7 11 7 11 7-4 7-11 7-11-7-11-7z"/><circle cx="12" cy="12" r="3"/></svg>
            Admin preview — viewing as a client
          </span>
          <button onClick={() => { onExitPreview(); navigate('/admin'); }} style={{ background: '#F2F6F7', color: '#1F4E5A', border: 'none', borderRadius: 8, padding: '7px 16px', fontSize: 13, fontWeight: 600, cursor: 'pointer', fontFamily: 'Inter, sans-serif' }}>
            Exit preview
          </button>
        </div>
      )}
    </>
  );
}

// Admin panel. AdminPanel self-gates (its own login screen / access-denied), so we
// just mount it and wire the "view site" / "view as client" buttons to routes.
function AdminRoute({ onEnterPreview }) {
  const navigate = useNavigate();
  return (
    <AdminPanel
      onGoToLanding={() => navigate('/')}
      onPreviewAsClient={() => { onEnterPreview(); navigate('/programs'); }}
    />
  );
}

export default function App() {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [isAdmin, setIsAdmin] = useState(false);
  const [loading, setLoading] = useState(true);
  const [adminPreview, setAdminPreview] = useState(false);

  useEffect(() => {
    const unsubAuth = onAuthStateChanged(auth, async (u) => {
      if (u) {
        setUser(u);
        try {
          const tokenResult = await u.getIdTokenResult();
          if (tokenResult.claims.superAdmin === true) {
            setIsAdmin(true);
            setLoading(false);
            return;
          }
          const adminSnap = await getDoc(doc(db, 'admins', u.uid));
          setIsAdmin(adminSnap.exists());
        } catch {
          setIsAdmin(false);
        }
        setLoading(false);
      } else {
        setUser(null);
        setIsAdmin(false);
        setAdminPreview(false);
        setLoading(false);
      }
    });
    return unsubAuth;
  }, []);

  if (loading) return <FullScreenSpinner />;

  // Landing "Enroll"/program-card handlers may receive a program object, a click
  // event, or nothing — only treat it as a program when it has an id.
  const handleEnroll = (program) =>
    navigate(program && program.id ? `/programs/${program.id}` : '/programs');
  const handleEnrollMasterclass = (mc) =>
    navigate(mc && mc.id ? `/masterclasses/${mc.id}` : '/programs');

  // The marketing landing page. Shared as both the logged-out home (`/`) and the
  // always-reachable `/welcome` so logged-in users can still browse/buy new programs.
  const marketingLanding = (
    <LandingPage
      user={user}
      isAdmin={isAdmin}
      onEnroll={handleEnroll}
      onEnrollMasterclass={handleEnrollMasterclass}
      onSignIn={() => navigate(user ? (isAdmin ? '/admin' : '/programs') : '/signin')}
      onGoToApp={() => navigate(isAdmin ? '/admin' : '/programs')}
    />
  );

  return (
    <Routes>
      {/* Returning users who type the bare domain land straight on their programs
          (admins on the panel) instead of the marketing site — the simplest "way
          back in" for non-tech users. Logged-out visitors still see the landing. */}
      <Route
        path="/"
        element={user ? <Navigate to={isAdmin ? '/admin' : '/programs'} replace /> : marketingLanding}
      />
      <Route path="/welcome" element={marketingLanding} />
      <Route path="/signin" element={<SigninRoute user={user} isAdmin={isAdmin} />} />
      {/* Public program page — shared links land here without a sign-in wall.
          Sign-in is required only at enrol/play (handled inside the page). */}
      <Route
        path="/programs/:programId"
        element={<PublicProgramPage user={user} adminPreview={adminPreview} isAdmin={isAdmin} />}
      />
      <Route path="/admin/*" element={<AdminRoute onEnterPreview={() => setAdminPreview(true)} />} />
      <Route
        path="/*"
        element={
          <ClientRoute
            user={user}
            isAdmin={isAdmin}
            adminPreview={adminPreview}
            onExitPreview={() => setAdminPreview(false)}
          />
        }
      />
    </Routes>
  );
}
